'use client';

import { SvgClose } from '@/components/icons-eataly';
import { cn } from '@/lib/utils';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { resolveImageAlt } from '@/lib/images/resolve-image-alt';
import { toGalleryZoomSrcs } from '@/lib/images/gallery-sizes';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';
import type { GalleryImage } from './types';
import { Button } from '../ui/button';

const ZOOM_SCALE = 2;
const DRAG_THRESHOLD_PX = 8;
/** Desktop thumbnail column: thumb `size-[104px]` + `gap-2` (8px) per step */
const ZOOM_THUMB_SCROLL_STEP_PX = 104 + 8;
/** Mobile carousel left inset; must match the first slide's `ml-6`. */
const MOBILE_CAROUSEL_INSET_PX = 24;

function clampPan(x: number, y: number, width: number, height: number, scale: number): { x: number; y: number } {
    const maxX = (width * (scale - 1)) / 2;
    const maxY = (height * (scale - 1)) / 2;
    return {
        x: Math.max(-maxX, Math.min(maxX, x)),
        y: Math.max(-maxY, Math.min(maxY, y)),
    };
}

interface ZoomableDialogImageProps {
    src: string;
    alt: string;
    className?: string;
}

function ZoomableDialogImage({ src, alt, className }: ZoomableDialogImageProps): ReactElement {
    const [zoomed, setZoomed] = useState(false);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLButtonElement>(null);
    const panRef = useRef(pan);
    panRef.current = pan;
    const zoomedRef = useRef(zoomed);
    zoomedRef.current = zoomed;
    const { t: tProduct } = useTranslation('product');
    const zoomSrcs = useMemo(() => toGalleryZoomSrcs(src), [src]);
    const sessionRef = useRef({
        pointerId: -1,
        startClientX: 0,
        startClientY: 0,
        startPanX: 0,
        startPanY: 0,
        dragStarted: false,
    });

    const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        sessionRef.current = {
            pointerId: e.pointerId,
            startClientX: e.clientX,
            startClientY: e.clientY,
            startPanX: panRef.current.x,
            startPanY: panRef.current.y,
            dragStarted: false,
        };
        setIsDragging(false);
        if (zoomed) {
            e.currentTarget.setPointerCapture(e.pointerId);
        }
    };

    const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
        if (sessionRef.current.pointerId !== e.pointerId) return;
        const dx = e.clientX - sessionRef.current.startClientX;
        const dy = e.clientY - sessionRef.current.startClientY;
        if (!sessionRef.current.dragStarted && dx * dx + dy * dy >= DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
            sessionRef.current.dragStarted = true;
            if (zoomed) {
                setIsDragging(true);
            }
        }
        if (!zoomed || !sessionRef.current.dragStarted) return;
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const s = sessionRef.current;
        setPan(clampPan(s.startPanX + dx, s.startPanY + dy, rect.width, rect.height, ZOOM_SCALE));
    };

    const endPointerSession = (e: React.PointerEvent<HTMLButtonElement>) => {
        if (sessionRef.current.pointerId !== e.pointerId) return;
        const hadDrag = sessionRef.current.dragStarted;
        const pid = e.pointerId;
        try {
            e.currentTarget.releasePointerCapture(pid);
        } catch {
            /* ignore */
        }
        sessionRef.current.pointerId = -1;
        setIsDragging(false);

        if (hadDrag) return;

        if (zoomedRef.current) {
            setZoomed(false);
            setPan({ x: 0, y: 0 });
        } else {
            setZoomed(true);
            setPan({ x: 0, y: 0 });
        }
    };

    const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
        endPointerSession(e);
    };

    const onPointerCancel = (e: React.PointerEvent<HTMLButtonElement>) => {
        const pid = sessionRef.current.pointerId;
        sessionRef.current.pointerId = -1;
        sessionRef.current.dragStarted = false;
        setIsDragging(false);
        try {
            if (pid >= 0) {
                e.currentTarget.releasePointerCapture(pid);
            }
        } catch {
            /* ignore */
        }
    };
    useEffect(() => {
        setZoomed(false);
        setPan({ x: 0, y: 0 });
        setIsDragging(false);
    }, [src]);

    return (
        <button
            type="button"
            ref={containerRef}
            className={cn(
                'relative aspect-square w-full overflow-hidden border-0 bg-transparent p-0',
                zoomed ? 'touch-none' : '',
                zoomed ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-zoom-in',
                className
            )}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            aria-label={zoomed ? tProduct('imageViewerZoomedPan') : tProduct('imageViewerTapToZoom')}>
            <picture className="size-full">
                <source media="(min-width: 768px)" srcSet={zoomSrcs.desktop} />
                <img
                    src={zoomSrcs.mobile}
                    alt={alt}
                    width={600}
                    height={600}
                    draggable={false}
                    loading="eager"
                    className="size-full object-cover object-center will-change-transform"
                    style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoomed ? ZOOM_SCALE : 1})`,
                        transformOrigin: 'center center',
                    }}
                />
            </picture>
        </button>
    );
}

export interface ImageGalleryZoomDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    images: GalleryImage[];
    imageAltFallback: string;
    /** Index to show when the dialog opens */
    initialIndex: number;
    onSyncSelection: (index: number) => void;
    isLabelGallery?: boolean;
    title?: string;
    description?: string;
}

export function ImageGalleryZoomDialog({
    open,
    onOpenChange,
    images,
    imageAltFallback,
    initialIndex,
    onSyncSelection,
    isLabelGallery,
    title,
    description,
}: ImageGalleryZoomDialogProps): ReactElement {
    const [zoomIndex, setZoomIndex] = useState(initialIndex);
    const thumbColRef = useRef<HTMLDivElement>(null);
    const [showDesktopArrows, setShowDesktopArrows] = useState({ top: false, bottom: images.length > 5 });

    const [zoomEmblaRef, zoomEmblaApi] = useEmblaCarousel({
        align: () => MOBILE_CAROUSEL_INSET_PX,
        containScroll: 'trimSnaps',
        dragFree: false,
    });

    useLayoutEffect(() => {
        if (!open) return;
        setZoomIndex(initialIndex);
        setShowDesktopArrows({ top: false, bottom: images.length > 5 });
    }, [open, initialIndex, images.length]);

    useEffect(() => {
        if (!open || !zoomEmblaApi) return;
        zoomEmblaApi.scrollTo(zoomIndex, true);
        const thumb = document.getElementById(`zoom-thumb-${zoomIndex}`);
        if (thumb) {
            thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [open, zoomEmblaApi, zoomIndex, images.length]);

    useEffect(() => {
        if (!zoomEmblaApi || !open) return;
        const syncIndex = () => {
            const i = zoomEmblaApi.selectedScrollSnap();
            setZoomIndex((prev) => (prev === i ? prev : i));
        };
        zoomEmblaApi.on('select', syncIndex);
        zoomEmblaApi.on('reInit', syncIndex);
        return () => {
            zoomEmblaApi.off('select', syncIndex);
            zoomEmblaApi.off('reInit', syncIndex);
        };
    }, [zoomEmblaApi, open]);

    const handleZoomOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            onSyncSelection(zoomIndex);
        }
        onOpenChange(nextOpen);
    };

    const scrollZoomThumbsBy = (delta: number) => {
        thumbColRef.current?.scrollBy({ top: delta, behavior: 'smooth' });
    };

    const { t: tProduct } = useTranslation('product');

    const activeZoom = images[zoomIndex];
    /** A lone image has nothing to scroll to: render it full-bleed, without mounting the carousel */
    const singleImage = images.length === 1 ? images[0] : undefined;

    return (
        <Dialog open={open} onOpenChange={handleZoomOpenChange}>
            <DialogContent
                showCloseButton={false}
                className={cn(
                    'bg-neutral-white relative z-50 flex h-fit max-h-[100dvh] w-full !max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 shadow-none',
                    'fixed inset-0 top-[unset] bottom-0 left-0 translate-x-0 translate-y-0 rounded-t-lg',
                    // Mobile: slide only (kill Dialog default fade/zoom that look like a fade-out)
                    'max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=closed]:slide-out-to-bottom',
                    'max-md:data-[state=closed]:duration-300 max-md:data-[state=open]:duration-500',
                    'max-md:data-[state=open]:[--tw-enter-opacity:1] max-md:data-[state=closed]:[--tw-exit-opacity:1]',
                    'max-md:data-[state=open]:[--tw-enter-scale:1] max-md:data-[state=closed]:[--tw-exit-scale:1]',
                    'md:inset-auto md:top-1/2 md:left-1/2 md:h-auto md:max-h-[90vh] md:min-h-0 md:w-auto md:max-w-fit md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg',
                    !isLabelGallery && 'md:flex-row'
                )}>
                <DialogClose asChild>
                    <button
                        type="button"
                        className={cn(
                            'absolute top-6 right-6 z-20 flex size-10 shrink-0 items-center justify-center rounded-full md:size-[52px]',
                            isLabelGallery ? 'bg-neutral-white' : 'bg-sand-95'
                        )}
                        aria-label={tProduct('closeImageViewer')}>
                        <SvgClose className="size-5 shrink-0" aria-hidden />
                    </button>
                </DialogClose>
                <div className={cn(isLabelGallery ? 'flex items-center p-6 pr-20 pb-3 md:pr-24' : 'sr-only')}>
                    <DialogTitle className={cn('heading-title-m')}>{title}</DialogTitle>
                    {description && <DialogDescription className="sr-only">{description}</DialogDescription>}
                </div>
                <div className="flex min-h-0 flex-1 flex-col pt-3 pb-8 md:hidden">
                    {images.length > 1 ? (
                        <>
                            <div className="min-h-0 flex-1 overflow-hidden" ref={zoomEmblaRef}>
                                <div className="flex h-full gap-2">
                                    {images.map((image, index) => (
                                        <div
                                            key={`zoom-${image.src}`}
                                            className={cn(
                                                'w-[calc(100%-56px)] shrink-0 self-center',
                                                index === 0 && (isLabelGallery ? 'ml-6' : 'ml-3'),
                                                // Embla reads the last slide's end margin as the scroll end inset.
                                                index === images.length - 1 && (isLabelGallery ? 'mr-6' : 'mr-3')
                                            )}>
                                            <ZoomableDialogImage
                                                src={image.src}
                                                alt={resolveImageAlt({ alt: image.alt, fallback: imageAltFallback })}
                                                className={cn(!isLabelGallery && 'rounded-lg')}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex gap-1 px-4 pt-6">
                                {images.map((image, index) => (
                                    <button
                                        key={`zoom-dot-${image.src}-${image.thumbSrc ?? ''}`}
                                        type="button"
                                        className="block px-2 py-1 cursor-pointer"
                                        aria-label={`${String(tProduct('imageViewerTitle'))} ${index + 1}`}
                                        onClick={() => {
                                            setZoomIndex(index);
                                            zoomEmblaApi?.scrollTo(index, true);
                                        }}>
                                        <span
                                            className={cn(
                                                'block size-2 rounded-md border',
                                                zoomIndex === index ? 'bg-[var(--neutral-10)]' : 'bg-transparent'
                                            )}
                                        />
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : (
                        singleImage && (
                            <div className={cn('flex min-h-0 flex-1 items-center px-3', isLabelGallery && 'px-6')}>
                                <ZoomableDialogImage
                                    src={singleImage.src}
                                    alt={resolveImageAlt({ alt: singleImage.alt, fallback: imageAltFallback })}
                                    className={cn(!isLabelGallery && 'rounded-lg')}
                                />
                            </div>
                        )
                    )}
                </div>

                <div className="hidden gap-4 p-3 md:flex md:h-auto">
                    <div className="relative flex flex-col items-center gap-2 shrink-0 overflow-auto">
                        <div
                            ref={thumbColRef}
                            className="flex flex-col gap-2 overflow-y-auto overscroll-contain snap-y [scrollbar-width:none] max-h-[612px] sticky top-0"
                            onScroll={(e) => {
                                if (e.currentTarget.scrollTop <= 0) {
                                    setShowDesktopArrows({ top: false, bottom: true });
                                } else if (
                                    e.currentTarget.scrollTop + e.currentTarget.clientHeight >=
                                    e.currentTarget.scrollHeight
                                ) {
                                    setShowDesktopArrows({ top: true, bottom: false });
                                } else {
                                    setShowDesktopArrows({ top: true, bottom: true });
                                }
                            }}>
                            {images.map((image, index) => (
                                <button
                                    // eslint-disable-next-line react/no-array-index-key -- gallery order from catalog; src may repeat across variants
                                    key={`zoom-thumb-${image.src}-${index}`}
                                    id={`zoom-thumb-${index}`}
                                    type="button"
                                    onClick={() => setZoomIndex(index)}
                                    className={cn('shrink-0 snap-start p-1 cursor-pointer')}
                                    aria-label={`${resolveImageAlt({ alt: image.alt, fallback: imageAltFallback })} (${index + 1} / ${images.length})`}
                                    aria-pressed={zoomIndex === index}>
                                    <div
                                        className={cn(
                                            'aspect-square overflow-hidden relative size-[104px] max-md:hidden outline-offset-2 outline-transparent rounded-lg',
                                            zoomIndex === index && 'outline-primary outline-2'
                                        )}>
                                        <img
                                            src={image.thumbSrc || image.src}
                                            alt={resolveImageAlt({ alt: image.alt, fallback: imageAltFallback })}
                                            className="size-full object-cover object-center"
                                            loading="lazy"
                                        />
                                    </div>
                                </button>
                            ))}
                        </div>
                        {showDesktopArrows.top && (
                            <Button
                                type="button"
                                className="flex-shrink-0 mx-1 absolute top-0"
                                onClick={() => scrollZoomThumbsBy(-ZOOM_THUMB_SCROLL_STEP_PX)}
                                iconOnly
                                aria-label={tProduct('scrollThumbnailsUp')}>
                                <ChevronUp className="size-6" aria-hidden />
                            </Button>
                        )}
                        {showDesktopArrows.bottom && (
                            <Button
                                type="button"
                                className="flex-shrink-0 mx-1 absolute bottom-0"
                                onClick={() => scrollZoomThumbsBy(ZOOM_THUMB_SCROLL_STEP_PX)}
                                iconOnly
                                aria-label={tProduct('scrollThumbnailsDown')}>
                                <ChevronDown className="size-6" aria-hidden />
                            </Button>
                        )}
                    </div>
                    {activeZoom && (
                        <ZoomableDialogImage
                            src={activeZoom.src}
                            alt={resolveImageAlt({ alt: activeZoom.alt, fallback: imageAltFallback })}
                            className="rounded-md shrink-0 md:size-[min(612px,calc(90vh-8rem))]"
                        />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
